import React, { useState, useEffect } from 'react';
import { Menu, X, Mail, Phone } from 'lucide-react';
import { companyInfo } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-black/95 backdrop-blur-md shadow-[0_4px_30px_rgba(212,160,23,0.1)]' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div 
            className="flex items-center gap-3 cursor-pointer group"
            onClick={() => scrollToSection('hero')}
          >
            <img
              src={companyInfo.logo}
              alt={companyInfo.name}
              className="h-12 w-12 object-contain rounded-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(212,160,23,0.5)]"
            />
            <span className="text-lg font-bold text-gold-gradient hidden sm:block group-hover:animate-textGlow">
              {companyInfo.name}
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {['Home', 'Fleet', 'About', 'Reviews', 'Inquiry'].map((item, index) => (
              <button
                key={item}
                onClick={() => scrollToSection(item.toLowerCase() === 'home' ? 'hero' : item.toLowerCase())}
                className="relative text-white/80 hover:text-amber-400 transition-all duration-300 font-medium link-underline"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {item}
              </button>
            ))}
          </nav>

          {/* Contact Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href={`tel:${companyInfo.phone.replace(/-/g, '')}`}
              className="group flex items-center gap-2 text-amber-400 hover:text-amber-300 font-medium transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden lg:inline">{companyInfo.phone}</span>
            </a>
            <a
              href={`mailto:${companyInfo.email}`}
              className="group flex items-center gap-2 btn-gold px-5 py-2.5 rounded-lg overflow-hidden"
            >
              <Mail className="w-4 h-4 transition-transform duration-300 group-hover:rotate-12" />
              <span>Contact Us</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2 hover:text-amber-400 transition-colors"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 animate-scaleIn" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden overflow-hidden transition-all duration-500 ${
          isMobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="bg-black/95 backdrop-blur-md rounded-lg mb-4 p-4 border border-amber-500/20">
            <nav className="flex flex-col gap-2">
              {['Home', 'Fleet', 'About', 'Reviews', 'Inquiry'].map((item, index) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase() === 'home' ? 'hero' : item.toLowerCase())}
                  className="text-white/80 hover:text-amber-400 hover:bg-amber-500/10 transition-all duration-300 font-medium text-left py-3 px-4 rounded-lg"
                  style={{ 
                    animation: isMobileMenuOpen ? `fadeInLeft 0.3s ease-out forwards` : 'none',
                    animationDelay: `${index * 50}ms`,
                    opacity: isMobileMenuOpen ? 1 : 0
                  }}
                >
                  {item}
                </button>
              ))}
              <div className="flex flex-col gap-2 mt-2 pt-2 border-t border-gray-800">
                <a
                  href={`tel:${companyInfo.phone.replace(/-/g, '')}`}
                  className="flex items-center justify-center gap-2 py-3 text-amber-400 font-medium"
                >
                  <Phone className="w-4 h-4" />
                  {companyInfo.phone}
                </a>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center justify-center gap-2 btn-gold px-5 py-3 rounded-lg"
                >
                  <Mail className="w-4 h-4" />
                  Email Us
                </a>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
